/**
 * wp-custom entry point
 * Add your custom TypeScript code here
 */

declare const routes: {
  cart_url: string;
  cart_update_url: string;
};

declare const PUB_SUB_EVENTS: {
  cartUpdate: string;
};

declare function subscribe(
  eventName: string,
  callback: (data?: any) => void,
): () => void;

/**
 * Updates the PreDiscountBetween400And475 cart attribute based on cart subtotal.
 * This ensures the attribute is always current, even when users modify cart and return to checkout.
 */
async function updatePreDiscountAttribute(_cartData?: any): Promise<void> {
  try {
    // Always fetch fresh cart data to ensure we have latest subtotal
    // The cartData from events may be stale or from before section re-render
    const response = await fetch(`${routes.cart_url}.json`);
    const cart = await response.json();

    const subtotal = cart.items_subtotal_price; // in cents
    const inRange = subtotal > 39900 && subtotal <= 47500;
    const newValue = inRange ? "1" : "0";

    // Check current value
    const currentValue = cart.attributes?.PreDiscountBetween400And475;

    console.debug(
      `[SOSC] Checking attribute: current=${currentValue}, needed=${newValue} (subtotal: $${(subtotal / 100).toFixed(2)})`,
    );

    if (currentValue === newValue) {
      return;
    }

    // Update the cart attribute
    const updateResponse = await fetch(routes.cart_update_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        attributes: {
          PreDiscountBetween400And475: newValue,
        },
      }),
    });

    await updateResponse.json();

    // Verify the update took effect
    const verifyResponse = await fetch(`${routes.cart_url}.json`);
    const verifyCart = await verifyResponse.json();
    const verifiedValue = verifyCart.attributes?.PreDiscountBetween400And475;

    console.debug(
      `[SOSC] Updated PreDiscountBetween400And475: ${currentValue} → ${newValue}, verified: ${verifiedValue}`,
    );

    if (verifiedValue !== newValue) {
      console.error(
        `[SOSC] Attribute update failed! Expected ${newValue}, got ${verifiedValue}`,
      );
    }
  } catch (error) {
    console.error("[SOSC] Failed to update cart attribute:", error);
  }
}

// Initialize when DOM is ready
function init() {
  console.debug("[SOSC] Initializing cart attribute tracking...");

  // Update attribute on page load
  updatePreDiscountAttribute();

  // Subscribe to cart updates using Dawn's pubsub system
  if (
    typeof subscribe === "function" &&
    typeof PUB_SUB_EVENTS !== "undefined"
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      console.debug("[SOSC] Cart update event received:", event?.source);
      // Use cartData from event if available, otherwise fetch fresh
      updatePreDiscountAttribute(event?.cartData);
    });
    console.debug("[SOSC] Subscribed to cart-update events");
  } else {
    console.warn("[SOSC] PubSub not available, falling back to polling");
    // Fallback: check for pubsub availability
    let attempts = 0;
    const maxAttempts = 50;
    const checkPubSub = setInterval(() => {
      attempts++;
      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        clearInterval(checkPubSub);
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          console.debug(
            "[SOSC] Cart update event received (delayed init):",
            event?.source,
          );
          updatePreDiscountAttribute(event?.cartData);
        });
        console.debug("[SOSC] Subscribed to cart-update events (delayed)");
      } else if (attempts >= maxAttempts) {
        clearInterval(checkPubSub);
        console.error("[SOSC] PubSub never became available");
      }
    }, 100);
  }
}

// Run init when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

export { updatePreDiscountAttribute };
